"use client";

import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../../assets/optimized/logo.webp";
import { IoPerson } from "react-icons/io5";
import { FaBars, FaTimes } from "react-icons/fa";
import { toast } from "sonner";
import useAuth from "../../services/useAuth";
import { useUser } from "../../context/UserContext";

// ── Pages that have a full-bleed hero image directly below the nav ────────────
// On these pages the nav starts transparent and transitions to solid on scroll.
// Every other page (blogs, individual blog posts, etc.) starts solid immediately.
const HERO_PAGES = ["/", "/packages", "/about-us", "/contact-us", "/permits"];

const hasHero = (pathname) =>
  HERO_PAGES.some((p) => (p === "/" ? pathname === "/" : pathname === p || pathname.startsWith(p + "/")));

const Navbar = () => {
  const { logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();

  const onHeroPage = hasHero(location.pathname);

  // ── Scroll detection ──────────────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll(); // initialise on mount
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // On route change: close menu + re-evaluate scroll position
  useEffect(() => {
    setIsMenuOpen(false);
    setScrolled(window.scrollY > 60);
  }, [location.pathname]);

  const toggleMenu = () => setIsMenuOpen((v) => !v);

  const isActive = (path) => {
    if (path === "/packages") {
      return location.pathname === "/packages" || location.pathname.startsWith("/package/");
    }
    return location.pathname === path;
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Logout successful");
      navigate("/");
    } catch {
      toast.error("Logout failed");
    }
  };

  const getDashboardLink = () => {
    if (!user) return "/login";
    return user.role === "admin" ? "/admin" : "/packages";
  };

  // ── isSolid: true when scrolled past threshold OR on a non-hero page ─────
  const isSolid = scrolled || !onHeroPage;

  // ── Styles ─────────────────────────────────────────────────────────────────
  const linkBase = "text-[15px] uppercase tracking-[0.22em] font-bold transition-all duration-300";

  const activeLinkClass = isSolid
    ? `${linkBase} px-4 py-1.5 rounded-full bg-[#1a3a52]/10 text-[#1a3a52] border border-[#1a3a52]/20`
    : `${linkBase} px-4 py-1.5 rounded-full bg-white/15 text-white border border-white/30 backdrop-blur-sm`;

  const inactiveLinkClass = isSolid
    ? `${linkBase} text-[#1a3a52]/70 hover:text-[#1a3a52]`
    : `${linkBase} text-white/85 hover:text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.4)]`;

  const linkClass = (path) => (isActive(path) ? activeLinkClass : inactiveLinkClass);

  const authLinkClass = isSolid
    ? "flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[#1a3a52]/75 hover:text-[#1a3a52] transition-colors"
    : "flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-white/90 hover:text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.4)] transition-colors";

  const mobileIconClass = (path) =>
    `text-[13px] uppercase tracking-[0.18em] font-bold transition-colors ${
      isSolid
        ? isActive(path) ? "text-[#1a3a52]" : "text-[#1a3a52]/60"
        : isActive(path) ? "text-white" : "text-white/75"
    }`;

  return (
    <>
      {/* ── Main nav ──────────────────────────────────────────────────────── */}
      <nav
        className={`w-full fixed top-0 left-0 z-[1000] px-6 lg:px-20 flex items-center justify-between transition-all duration-500 ${
          isSolid
            ? "py-3.5 bg-white/92 backdrop-blur-xl shadow-[0_2px_24px_rgba(0,0,0,0.07)] border-b border-black/[0.05]"
            : "py-5 bg-transparent"
        }`}
      >
        {/* LOGO */}
        <Link to="/" className="z-[1001] shrink-0">
          <img
            src={logo}
            alt="logo"
            className={`transition-all duration-500 ${isSolid ? "w-12 lg:w-14" : "w-14 lg:w-16"}`}
          />
        </Link>

        {/* DESKTOP LINKS */}
        <div className="hidden lg:flex items-center gap-8">
          <Link to="/"           className={linkClass("/")}>Home</Link>
          <Link to="/packages"   className={linkClass("/packages")}>Packages</Link>
          <Link to="/blogs"      className={linkClass("/blogs")}>Blogs</Link>
          <Link to="/about-us"   className={linkClass("/about-us")}>About</Link>
          <Link to="/contact-us" className={linkClass("/contact-us")}>Contact</Link>
          <Link to="/permits"    className={linkClass("/permits")}>Guide</Link>
        </div>

        {/* DESKTOP AUTH */}
        <div className="hidden lg:flex items-center gap-6">
          {user ? (
            <div className="flex items-center gap-5">
              <button onClick={() => navigate(getDashboardLink())} className={authLinkClass}>
                <IoPerson size={16} />
                Dashboard
              </button>
              <button
                onClick={handleLogout}
                className={`text-[11px] uppercase tracking-[0.22em] font-bold transition-colors text-[#C99D89] ${
                  isSolid ? "hover:text-[#b88870]" : "hover:text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.4)]"
                }`}
              >
                Logout
              </button>
            </div>
          ) : (
            <Link to="/login" className={authLinkClass}>
              <IoPerson size={16} />
              Sign In
            </Link>
          )}
        </div>

        {/* MOBILE CONTROLS */}
        <div className="lg:hidden flex items-center gap-4 z-[1001]">
          <Link to="/"         className={mobileIconClass("/")}>Home</Link>
          <Link to="/packages" className={mobileIconClass("/packages")}>Packages</Link>
          <Link
            to={getDashboardLink()}
            className={`transition-colors ${isSolid ? "text-[#1a3a52]/60" : "text-white/75"}`}
          >
            {user
              ? <IoPerson size={17} />
              : <span className="text-[10px] uppercase tracking-[0.15em] font-bold">Sign In</span>
            }
          </Link>
          <button
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
            className={`text-xl p-1 transition-colors ${isSolid ? "text-[#1a3a52]/70" : "text-white/85"}`}
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </nav>

      {/* ── Dark scrim — only on hero pages while nav is transparent ────────── */}
      <AnimatePresence>
        {onHeroPage && !isSolid && (
          <motion.div
            key="scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed top-0 left-0 w-full z-[999] pointer-events-none"
            style={{
              height: "160px",
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.30) 0%, rgba(0,0,0,0.06) 65%, transparent 100%)",
            }}
          />
        )}
      </AnimatePresence>

      {/* ── Mobile full screen menu ───────────────────────────────────────── */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", damping: 26, stiffness: 200 }}
            className="fixed inset-0 z-[10000] lg:hidden flex flex-col pt-32 px-10 gap-8"
            style={{ background: "linear-gradient(135deg, #0f1e2e 0%, #2e6a9e 100%)" }}
          >
            <button
              onClick={toggleMenu}
              className="fixed top-6 right-6 z-[10001] w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-[#FCFCFC] hover:border-[#C99D89] hover:text-[#C99D89] transition-all"
            >
              <FaTimes size={16} />
            </button>

            <div className="flex flex-col gap-8">
              {[
                { to: "/",           label: "Home" },
                { to: "/packages",   label: "Packages" },
                { to: "/blogs",      label: "Blogs" },
                { to: "/about-us",   label: "About Us" },
                { to: "/contact-us", label: "Contact Us" },
                { to: "/permits",    label: "Information Guide" },
              ].map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={toggleMenu}
                  className={`text-3xl font-serif transition-colors ${
                    isActive(to) ? "text-[#C99D89]" : "text-[#FCFCFC] hover:text-[#C99D89]"
                  }`}
                >
                  {label}
                </Link>
              ))}
            </div>

            <div className="h-px w-full bg-white/10 my-2" />

            <div className="mt-auto pb-16 flex flex-col gap-4">
              {user ? (
                <>
                  <button
                    onClick={() => { navigate(getDashboardLink()); toggleMenu(); }}
                    className="w-full py-5 bg-[#C99D89] text-[#FCFCFC] rounded-2xl font-bold uppercase tracking-[0.2em] text-[10px]"
                  >
                    My Dashboard
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full py-5 border border-white/10 text-[#FCFCFC] rounded-2xl font-bold uppercase tracking-[0.2em] text-[10px]"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <Link to="/login"  onClick={toggleMenu} className="py-5 bg-white/5 border border-white/10 text-[#FCFCFC] rounded-2xl text-center font-bold uppercase tracking-[0.2em] text-[10px]">Login</Link>
                  <Link to="/signup" onClick={toggleMenu} className="py-5 bg-[#C99D89] text-[#FCFCFC] rounded-2xl text-center font-bold uppercase tracking-[0.2em] text-[10px]">Sign Up</Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;